import animate from 'animejs'
import MinecraftBlockBreakerAPNG from './minecraft_block_breaker_apng'
import { MinecraftBlockBreakerInstructions as AudioController } from '../minecraft_block_breaker_instructions/minecraft_block_breaker_instructions'
import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants'

export default class MinecraftBlockBreakerBlock {
  /**
   * Contains the color palettes for each type of block
   * @type {Object}
   */
  static colors = {
    wood: ['#6A4E1A', '#8B6B26', '#1B1202', '#4D3A10', '#352A0C'],
    dirt: ['#705a49', '#49750F', '#867054', '#7D674F', '#9D875F'],
    stone: ['#5D5D5D', '#474747', '#6E6E6E', '#2E2D2C']
  }

  /**
   * The square dimensions of each block
   * @type {Int}
   */
  static size = 16

  /**
   * The total time in milliseconds for the crack animation to play
   * @type {Int}
   */
  static crackDuration = 300

  /**
   * The amount of time in milliseconds for each block flicker transition
   * @type {Int}
   */
  static flickerDuration = 500 // 1500

  /**
   * The percentage of pixels on a block to flicker
   * @type {Float}
   */
  static flickerPercentage = 0.8 // 0.2

  /**
   * A globally tracked value used as a z-index for blocks.
   *
   * This is used to make sure that block breaking animations appear above other
   * blocks. It is incremented each time it's used.
   *
   * @type {Int}
   */
  static zIndex = 10

  /**
   * A flag used to set when the flicker animation should be paused
   * @type {Boolean}
   */
  pauseFlicker = false

  /**
   * The number of pixels in a block
   * @type {Int}
   */
  pixelCount = MinecraftBlockBreakerBlock.size ** 2

  /**
   * A precalculated value for half the number of pixels per block, rounded down
   * @type {Int}
   */
  countHalf = Math.floor((MinecraftBlockBreakerBlock.size ** 2) / 2)

  /**
   * A precalculated value for half the number of pixels per dimension, rounded down
   * @type {Int}
   */
  sideHalf = Math.floor(MinecraftBlockBreakerBlock.size / 2)

  /**
   * A flag used to set when block interactions should be ignored
   * @type {Boolean}
   */
  pauseInteraction = false

  /**
   * Tracks the state of the block
   *
   * Values are from `"default"|"cracked"|"broken"`
   *
   * @type {string}
   */
  state = 'default'

  /**
   * An array of the pixels in this block
   * @type {Array}
   */
  pixels = []

  /**
   * An array of all flicker animations used in the block
   */
  get animations() {
    return document.getAnimations().filter(animation => {
      return animation.id === 'pixel-flicker' && this.element.contains(animation.effect.target)
    })
  }

  /**
   * True when all the conditions for animating the block are true
   */
  get animationCanPlay() {
    return (
      this.element.dataset.inView === undefined ||
      this.element.dataset.inView === 'true'
    ) &&
      !document.documentElement.classList.contains('reduced-motion') &&
      this.pauseFlicker === false
  }

  /**
   * The color palette for this block instance
   */
  get palette() {
    if (this.material !== 'generic') {
      return MinecraftBlockBreakerBlock.colors[this.material] || MinecraftBlockBreakerBlock.colors.wood
    } else {
      return this.element.dataset.colors.split(',')
    }
  }

  constructor (element) {
    this.element = element
    this.hasItem = 'itemid' in element.dataset
    this.material = element.dataset.type || 'wood'
    this.buttonLabel = element.querySelector('.js-button-label')
    this.gridContainer = element.querySelector('.js-block-grid')
    this.audioKey = element.dataset.audioKey || this.material
    this.emitter = GLOBAL_CONSTANTS.UTILS.EMITTER

    AudioController.loadAudio(this.element.dataset.audioKey)

    this.observer = new IntersectionObserver(observers => {
      const observer = observers[0]
      if (!observer) return

      this.element.dataset.inView = observer.isIntersecting

      this.toggleFlicker()
    }, {
      root: null,
      rootMargin: '0%',
      threshold: 0.5
    })

    if (this.hasItem) {
      this.setIconImage()
    }

    // Random seeds for dispersal
    this.randomA = [...Array(this.pixelCount)].map(_ => Math.floor(5 * Math.random()))
    this.randomB = [...Array(this.pixelCount)].map(_ => Math.floor(5 * Math.random()))

    this.observer.observe(this.element)

    this.emitter.on(GLOBAL_CONSTANTS.EVENTS.REDUCED_MOTION_CHANGE, () => {
      this.toggleFlicker()

      /* Bad fix alert:
       *
       * Calling only once was resulting in a handful of pixels still animating.
       * I don't have time to suss out the cause but suspect it to be something
       * like a conflict between when this cancels an animation and when the
       * animation re-ups itself. So calling again after a delay isn't a great
       * fix but it's good enough.
       */
      setTimeout(() => {
        this.toggleFlicker()
      }, 500)
    })

    MinecraftBlockBreakerBlock.modalController.modal.addEventListener('beforetoggle', (event) => {
      if (event.newState === 'open') {
        this.pauseFlicker = true
      } else {
        this.pauseFlicker = false
      }

      this.toggleFlicker()
    })

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.pauseFlicker = true
      } else {
        this.pauseFlicker = false
      }

      this.toggleFlicker()
    })

    this.createTexture()
  }

  /**
   * Sets the icon hidden behind the block
   * @returns {Promise}
   */
  setIconImage = async () => {
    const icon = await new MinecraftBlockBreakerAPNG(
      this.element.dataset.blockIconSource,
      '',
      !!this.element.dataset.blockIconIsApng
    )

    icon.element?.classList.add('block-icon')

    this.element.appendChild(icon.element)
  }

  /**
   * Initializes the block texture
   */
  createTexture = () => {
    this.element.style.setProperty('--grid-size', MinecraftBlockBreakerBlock.size)

    for (let i = 0; i < this.pixelCount; i++) {
      const pixel = document.createElement('div')
      pixel.style.setProperty('background-color', this.palette[Math.floor(Math.random() * this.palette.length)])
      pixel.dataset.row = Math.floor(i / MinecraftBlockBreakerBlock.size)
      pixel.dataset.column = i % MinecraftBlockBreakerBlock.size

      this.gridContainer.appendChild(pixel)
      this.pixels.push(pixel)

      if (Math.random() < MinecraftBlockBreakerBlock.flickerPercentage) {
        this.setFlickerAnimation(pixel, true)
      }
    }

    this.toggleFlicker()

    delete this.element.dataset.init

    this.buttonLabel.innerHTML = `Block of ${this.material}.`
  }

  /**
   * Toggles the play/pause state of the flicker animation
   */
  toggleFlicker = () => {
    if (this.animationCanPlay) {
      for (const animation of this.animations) {
        animation.play()
      }
    } else {
      for (const animation of this.animations) {
        animation.pause()
      }
    }
  }

  /**
   * Sets flicker animation for an individual pixel
   * @param {HTMLElement} element - The pixel to animate
   * @param {Boolean} firstTime - Tells the method that this is the initiation of the flicker animation
   */
  setFlickerAnimation = (element, firstTime) => {
    const originalFill = element.style.backgroundColor

    const keyframes = [
      {
        offset: 0,
        backgroundColor: originalFill
      },
      {
        offset: 0.5,
        backgroundColor: this.palette[Math.floor(Math.random() * this.palette.length)]
      },
      {
        offset: 1,
        backgroundColor: originalFill
      }
    ]

    const options = {
      id: 'pixel-flicker',
      delay: 1500 * Math.random(),
      fill: 'none',
      iterationStart: 0,
      iterations: 1,
      duration: MinecraftBlockBreakerBlock.flickerDuration,
      direction: 'normal',
      easing: 'linear'
    }

    const animation = element.animate(keyframes, options)

    animation.addEventListener('finish', () => {
      this.setFlickerAnimation(element, false)
    })

    if (firstTime) {
      animation.pause()
    }
  }

  /**
   * Handler triggered by interacting with a block
   */
  click = () => {
    if (this.pauseInteraction) return

    switch (this.state) {
      case 'cracked':
        this.shatterBlock()
        break
      case 'broken':
        this.onClickAfterBreak(this.element)
        break
      default:
        this.addCrack()
        break
    }
  }

  /**
   * Adds a crack and runs the appropriate animation
   */
  addCrack = () => {
    this.pauseInteraction = true

    let crackIndices

    this.buttonLabel.innerHTML = `Block of ${this.material} with cracks in it.`

    for (const animation of this.animations) {
      animation.cancel()
    }

    // If odd side length, get middle pixel
    if (MinecraftBlockBreakerBlock.size % 2 === 1) {
      crackIndices = [this.countHalf]
    // If even side length, get 2 of middle four
    } else {
      crackIndices = Math.random() > 0.5
        ? [this.countHalf - this.sideHalf - 1, this.countHalf + MinecraftBlockBreakerBlock.size - this.sideHalf]
        : [this.countHalf - this.sideHalf, this.countHalf + MinecraftBlockBreakerBlock.size - this.sideHalf - 1]
    }

    for (let i = 2; i <= Math.floor(MinecraftBlockBreakerBlock.size / 2); i++) {
      const ringIndices = this.pixels.map((pixel, pixelIndex) => {
        let {
          row,
          column
        } = pixel.dataset

        row = parseInt(row)
        column = parseInt(column)

        const left = this.sideHalf - i
        const right = this.sideHalf + i - 1
        const top = this.sideHalf - i
        const bottom = this.sideHalf + i - 1

        const use = (row === top && column >= left && column <= right) ||
          (row === bottom && column >= left && column <= right) ||
          (column === left && row >= top && row <= bottom) ||
          (column === right && row >= top && row <= bottom)

        return use ? pixelIndex : false
      }).filter(value => value !== false)

      const neighborhoods = crackIndices.map(w => MinecraftBlockBreakerBlock.neighbors(w))
      const unions = neighborhoods.map(arr => ringIndices.filter(q => new Set(arr).has(q)))
      let randos = Array.from(new Set(unions.map(arr => MinecraftBlockBreakerBlock.shuffle(arr).filter((_, j) => j < 2)).flat()))
      if (i > 2) {
        randos = randos.filter((_, j) => j % 3 < 2)
      }
      crackIndices = crackIndices.concat(randos)
    }

    this.animateCrack(crackIndices)

    this.observer.unobserve(this.element)

    this.pauseInteraction = false
    this.state = 'cracked'
  }

  /**
   * Breaks a block, runs the appropriate animation, and triggers a callback
   */
  shatterBlock = () => {
    this.element.style.zIndex = ++MinecraftBlockBreakerBlock.zIndex

    if (this.hasItem) {
      this.buttonLabel.innerHTML = `${this.element.dataset.blockIconAlt}.`
    } else {
      this.buttonLabel.innerHTML = 'Empty block.'
    }

    if (!this.hasItem && this.state !== 'broken') {
      const b = -10 * (this.hasItem ? 0.75 : 1)
      const k = 4 * (this.hasItem ? 0.75 : 1)
      const breakDuration = 1200 * (this.hasItem ? 0.3 : 1)
      const delayStagger = 300 * (this.hasItem ? 0.3 : 1)

      // scales the effect relative to the viewport size
      const scale = document.documentElement.clientWidth / 1920

      for (const animation of this.animations) {
        animation.cancel()
      }

      for (let i = 0; i < this.pixels.length; i++) {
        const pixel = this.pixels[i]

        if (Math.random() >= 0.55) {
          const a = i % 2 === 0 ? 10 * this.randomA[i] + i % 10 : -10 * this.randomA[i] + i % 10
          const c = -1 - this.randomB[i] / 2

          animate({
            targets: pixel,
            duration: breakDuration,
            delay: delayStagger * Math.random(),
            easing: 'linear',
            autoplay: true,
            update: ({ progress }) => {
              const t = progress / 100
              pixel.style.transform = `
                translateX(${k * t * a * scale}px)
                translateY(${k * ((b * t - c) ** 2 - c ** 2) * scale}px)
                scale(${1 + t * (0.5 * this.randomB[i])})
              `
              pixel.style.opacity = Math.max(0, Math.min(1, (1 - t) * 2)) ** 2
            }
          })
        } else {
          pixel.style.opacity = 0
        }
      }

      this.element.addEventListener('blur', () => {
        this.element.disabled = true
        this.element.tabindex = -1
      })

      this.state = 'broken'
      setTimeout(() => {
        this.gridContainer.remove()
        this.onBreak(this.element)
      }, breakDuration + delayStagger)
    } else {
      this.state = 'broken'
      this.gridContainer.remove()
      this.onBreak(this.element)
    }
  }

  /**
   * Runs the block cracking animations
   * @param {Array} indices - An array of indices for which pixels to crack
   */
  animateCrack = (indices) => {
    const delay = MinecraftBlockBreakerBlock.crackDuration / indices.length
    indices.forEach((pixelIndex, iteration) => {
      setTimeout(() => {
        this.pixels[pixelIndex]?.style.setProperty('background-color', Math.random() > 0.25 ? '#1f1f1f' : '#2f2f2f')
      }, delay * iteration)
    })
  }

  /**
   * A callback triggered when a block is broken
   */
  onBreak = () => {}

  /**
   * A callback triggered when a block is clicked after it has been broken
   */
  onClickAfterBreak = () => {}

  /**
   * Retuns the indices for the neighbors of the blocks at the given index
   * @type {Int}
   */
  static neighbors = (index) => {
    // don't worry about literal edge-cases yet, just start in the middle and never get there
    return [
      index - MinecraftBlockBreakerBlock.size - 1,
      index - MinecraftBlockBreakerBlock.size,
      index - MinecraftBlockBreakerBlock.size + 1,
      index - 1,
      index + 1,
      index + MinecraftBlockBreakerBlock.size - 1,
      index + MinecraftBlockBreakerBlock.size,
      index + MinecraftBlockBreakerBlock.size + 1
    ]
  }

  /**
   * Shuffles an array
   * @type {Array}
   */
  static shuffle = (arr) => {
    let m = arr.length
    while (m) {
      const i = Math.random() * m-- | 0
      const t = arr[m]
      arr[m] = arr[i]
      arr[i] = t
    }
    return arr
  }

  /**
   * The color palette for this block instance
   */
  static getPalette(element) {
    const material = element.dataset.type
    if (material !== 'generic') {
      return MinecraftBlockBreakerBlock.colors[material] || MinecraftBlockBreakerBlock.colors.wood
    } else {
      return element.dataset.colors.split(',')
    }
  }
}
